import moment from 'moment'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiFlexGroup, EuiFlexItem, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiSpacer, EuiSteps, EuiText, EuiTitle } from '@elastic/eui'
import { EuiStepInterface } from '@elastic/eui/src/components/steps/step'

import { useBeginCreateMasterCampaignPlanMutation, useGetMasterCampaignPlansQuery } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import SetBudgetStep from 'features/planner/SetBudgetStep'
import SetStartDateAndDuration from 'features/planner/SetStartDateAndDuration'

import { RootState } from '../../../app/rootReducer'
import history from '../../../services/HistoryService'
import { isValidURL } from '../../../utils/isValidUrl'
import { MasterCampaignPlanCreateStep, reset, resetWithUrl, setStep } from '../CreateMasterCampaignPlanSlice'
import { EnterUrlStep } from '../EnterUrlStep'
import { AdditionalDetailsStep } from './AdditionalDetailsStep'

export const MasterCampaignPlansCreatePage = () => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  })

  const { createMasterCampaignPlan } = useSelector((state: RootState) => state)
  const { currentOrganization, currentAccount } = useSelector((state: RootState) => state.app)
  const { data: plans } = useGetMasterCampaignPlansQuery({ organizationId: currentOrganization?.id ?? '' })
  const [createMasterCampaignPlanMutation, createMasterCampaignPlanMutationResult] = useBeginCreateMasterCampaignPlanMutation()
  const [showWarning, setShowWarning] = React.useState(false)
  const [warningText, setWarningText] = React.useState('')

  const dispatch = useDispatch()

  useEffect(() => {
    if (!currentAccount) {
      return
    }

    dispatch(resetWithUrl(currentAccount.website))
  }, [currentAccount, dispatch])

  useEffect(() => {
    return () => {
      dispatch(reset())
    }
  }, [dispatch])

  const callCreateMasterCampaignPlan = useCallback(async () => {
    if (!currentOrganization || !currentAccount) {
      return
    }
    await createMasterCampaignPlanMutation({
      organizationId: currentOrganization.id,
      params: {
        businessName: currentAccount.name,
        accountId: currentAccount.id,
        masterCampaignPlanName: `${currentAccount.name} ${createMasterCampaignPlan.durationInMonths} month plan`,
        website: createMasterCampaignPlan.url,
        aiRecommendedBudget: createMasterCampaignPlan.aiRecommendedBudget,
        targetMonthlyBudget: createMasterCampaignPlan.targetMonthlyBudget ?? 0,
        maxSingleBudget: createMasterCampaignPlan.maxSingleBudget,
        aiRecommendedStartAndDuration: createMasterCampaignPlan.aiRecommendedStartAndDuration,
        startDate: moment(createMasterCampaignPlan.startDate).toISOString(),
        durationInMonths: createMasterCampaignPlan.durationInMonths,
        details: createMasterCampaignPlan.details
      }
    })
  }, [createMasterCampaignPlan, currentOrganization, createMasterCampaignPlanMutation])

  const handleContinue = async () => {
    switch (createMasterCampaignPlan.step) {
      case MasterCampaignPlanCreateStep.SetUrl:
        dispatch(setStep(MasterCampaignPlanCreateStep.SetBudget))
        break
      case MasterCampaignPlanCreateStep.SetBudget:
        if ((!createMasterCampaignPlan.aiRecommendedBudget && !createMasterCampaignPlan.targetMonthlyBudget) || (createMasterCampaignPlan.targetMonthlyBudget && createMasterCampaignPlan.targetMonthlyBudget < 2500) || Number.isNaN(createMasterCampaignPlan.targetMonthlyBudget)) {
          setShowWarning((!createMasterCampaignPlan.aiRecommendedBudget && !createMasterCampaignPlan.targetMonthlyBudget) || (createMasterCampaignPlan.targetMonthlyBudget && createMasterCampaignPlan.targetMonthlyBudget < 2500) || Number.isNaN(createMasterCampaignPlan.targetMonthlyBudget))
          setWarningText('The minimum budget is $2500.')
        } else {
          setShowWarning(false)
          dispatch(setStep(MasterCampaignPlanCreateStep.SetStartDateAndDuration))
        }
        break
      case MasterCampaignPlanCreateStep.SetStartDateAndDuration:
        dispatch(setStep(MasterCampaignPlanCreateStep.AddAdditionalDetails))
        break
      case MasterCampaignPlanCreateStep.AddAdditionalDetails:
        await callCreateMasterCampaignPlan()
        history.push('/ai')
        break
      default:
        break
    }
  }

  const handleBack = () => {
    const currentPlans = plans ?? []

    switch (createMasterCampaignPlan.step) {
      case MasterCampaignPlanCreateStep.SetUrl:
        if (currentPlans.length > 0) {
          history.push('/ai')
        } else {
          history.push('/ai/welcome')
        }
        break
      case MasterCampaignPlanCreateStep.SetBudget:
        dispatch(setStep(MasterCampaignPlanCreateStep.SetUrl))
        break
      case MasterCampaignPlanCreateStep.SetStartDateAndDuration:
        dispatch(setStep(MasterCampaignPlanCreateStep.SetBudget))
        break
      case MasterCampaignPlanCreateStep.AddAdditionalDetails:
        dispatch(setStep(MasterCampaignPlanCreateStep.SetStartDateAndDuration))
        break
      default:
        break
    }
  }

  const getConfirmText = (step: MasterCampaignPlanCreateStep) => {
    switch (step) {
      case MasterCampaignPlanCreateStep.AddAdditionalDetails:
        return 'Generate Plan'
      default:
        return 'Continue'
    }
  }

  const shouldDisableNext = useCallback(() => {
    if (createMasterCampaignPlan.step === MasterCampaignPlanCreateStep.SetUrl) {
      return !isValidURL(createMasterCampaignPlan.url)
    }

    if (createMasterCampaignPlan.step === MasterCampaignPlanCreateStep.SetBudget) {
      return (!createMasterCampaignPlan.aiRecommendedBudget && !createMasterCampaignPlan.targetMonthlyBudget) || Number.isNaN(createMasterCampaignPlan.targetMonthlyBudget)
    }

    return undefined
  }, [createMasterCampaignPlan])

  const getStepStatus = (step: MasterCampaignPlanCreateStep) => {
    if (createMasterCampaignPlan.step === step) {
      return 'current'
    }

    switch (step) {
      case MasterCampaignPlanCreateStep.SetUrl:
        switch (createMasterCampaignPlan.step) {
          case MasterCampaignPlanCreateStep.SetBudget:
          case MasterCampaignPlanCreateStep.SetStartDateAndDuration:
          case MasterCampaignPlanCreateStep.AddAdditionalDetails:
            return 'complete'
          default:
            return 'incomplete'
        }
      case MasterCampaignPlanCreateStep.SetBudget:
        switch (createMasterCampaignPlan.step) {
          case MasterCampaignPlanCreateStep.SetStartDateAndDuration:
          case MasterCampaignPlanCreateStep.AddAdditionalDetails:
            return 'complete'
          default:
            return 'incomplete'
        }
      case MasterCampaignPlanCreateStep.SetStartDateAndDuration:
        switch (createMasterCampaignPlan.step) {
          case MasterCampaignPlanCreateStep.AddAdditionalDetails:
            return 'complete'
          default:
            return 'incomplete'
        }
    }

    return 'incomplete'
  }

  const steps: EuiStepInterface[] = [
    {
      title: 'Verify Website',
      status: getStepStatus(MasterCampaignPlanCreateStep.SetUrl),
      titleSize: 's',
      children:
        createMasterCampaignPlan.url && getStepStatus(MasterCampaignPlanCreateStep.SetUrl) === 'complete' ? (
          <>
            <EuiText size={'m'}>
              <p>
                <b>{currentAccount?.name}</b>
              </p>
            </EuiText>
            <EuiText size={'m'}>
              <p>{createMasterCampaignPlan.url}</p>
            </EuiText>
          </>
        ) : (
          <></>
        )
    },
    {
      title: 'Set Budget',
      status: getStepStatus(MasterCampaignPlanCreateStep.SetBudget),
      titleSize: 's',
      children:
        getStepStatus(MasterCampaignPlanCreateStep.SetBudget) === 'complete' ? (
          <EuiText size={'m'}>
            <p>{createMasterCampaignPlan.aiRecommendedBudget ? 'AdCritter AI will recommend a budget' : `${formatter.format(createMasterCampaignPlan.targetMonthlyBudget ?? 0)} /mo`}</p>
          </EuiText>
        ) : (
          <></>
        )
    },
    {
      title: 'Set Date and Duration',
      status: getStepStatus(MasterCampaignPlanCreateStep.SetStartDateAndDuration),
      titleSize: 's',
      children:
        createMasterCampaignPlan.startDate && createMasterCampaignPlan.durationInMonths && getStepStatus(MasterCampaignPlanCreateStep.SetStartDateAndDuration) === 'complete' ? (
          <EuiText size={'m'}>
            <p>{createMasterCampaignPlan.aiRecommendedStartAndDuration ? 'AdCritter AI will recommend a start date & duration' : `${createMasterCampaignPlan.durationInMonths} month plan starting on ${createMasterCampaignPlan.startDate}`}</p>
          </EuiText>
        ) : (
          <></>
        )
    },
    {
      title: 'Additional Details',
      status: getStepStatus(MasterCampaignPlanCreateStep.AddAdditionalDetails),
      titleSize: 's',
      children: <></>
    }
  ]

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>Create Master Campaign Plan: {currentAccount?.name}</h1>
            </EuiTitle>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false} className='eui-xScroll'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <>
                {createMasterCampaignPlan.step === MasterCampaignPlanCreateStep.SetUrl && <EnterUrlStep />}
                {createMasterCampaignPlan.step === MasterCampaignPlanCreateStep.SetBudget && <SetBudgetStep />}
                {createMasterCampaignPlan.step === MasterCampaignPlanCreateStep.SetStartDateAndDuration && <SetStartDateAndDuration />}
                {createMasterCampaignPlan.step === MasterCampaignPlanCreateStep.AddAdditionalDetails && <AdditionalDetailsStep />}
                <EuiSpacer />
                {showWarning && (
                  <>
                    <EuiCallOut color={'warning'} title={warningText} />
                    <EuiSpacer />
                  </>
                )}
                <EuiFlexGroup justifyContent={'flexStart'}>
                  <EuiFlexItem onClick={handleBack} grow={false}>
                    <EuiButton isDisabled={createMasterCampaignPlanMutationResult.isLoading}>Back</EuiButton>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButton fill onClick={handleContinue} isLoading={createMasterCampaignPlanMutationResult.isLoading} isDisabled={shouldDisableNext()}>
                      {getConfirmText(createMasterCampaignPlan.step)}
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup
                gutterSize={'none'}
                direction={'column'}
                justifyContent={'flexStart'}
                style={{
                  borderLeft: '1px solid #D3DAE6',
                  paddingLeft: '20px'
                }}>
                <EuiFlexItem grow={false}>
                  <EuiSteps
                    steps={steps}
                    style={{
                      marginTop: '62px',
                      width: 350
                    }}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiPageBody>
    </EuiPage>
  )
}
